// The 1-2-1 Mentorship, a private client space (ClientSpace.dc.html, both Tweaks; design 5.4 type 5;
// decisions 33 and 36), inside the product frame (src/product/ProductRoute.jsx draws the header, the
// menu and the footer; this draws the page).
//
// Who sees it:
// - the client, while their space is open: their own space, as "Laura, the client" draws it;
// - an owner or admin at 2-step login: Adam's view of 1 client's space, picked by ?space=<id> (the
//   link from /admin and from a question on Today), with the black bar and the boxes only Adam can
//   edit, as "Adam, as admin" draws it. An ended space stays readable for Adam, with nothing to edit;
// - staff viewing as the client see the client's page, and nothing can be changed.
//
// The pages: Home is the board's whole page. The menu's other pages (no board draws them) each show
// the board's own parts for it: Sessions (the next session, Book a session and the sessions),
// Recordings (the sessions with a recording), Resources (the shared files, then anything placed on
// the 1-2-1 itself, which every client sees), and Actions.
//
// At phone width (no board: PhoneLibrary's and PhoneWeek's patterns) the 2 side-by-side rows stack,
// the session rows' buttons go under the date and title, and Adam's boxes take the full width.
import { useEffect } from 'react'
import { useMember } from '../../app/member.jsx'
import { useNow } from '../../lib/clock.js'
import { usePhone } from '../../lib/media.js'
import { Link, navigate } from '../../lib/router.jsx'
import { useViewingAs } from '../../lib/viewAs.jsx'
import { SITE } from '../../config/site.js'
import { ResourcesSection } from '../../product/Resources.jsx'
import { COPY } from './copy.js'
import { UUID, eyebrowParts, firstName, nextSession } from './model.js'
import { useClientSpace } from './useClientSpace.js'
import { SessionRow } from './NextSession.jsx'
import { ActionsSection } from './Actions.jsx'
import { SessionsSection } from './Sessions.jsx'
import { FilesCard } from './Files.jsx'
import { AskAdamCard } from './AskAdam.jsx'
import { AdamBar, EndSpace } from './AdamView.jsx'
import { SpacePicker } from './SpaceList.jsx'
import '../../styles/mentorship.css'

// The space Adam last opened in this tab, so the menu's pages (which carry no ?space=) stay on it: 1 per
// private client space product, the 1-2-1 Mentorship's under its key as before
export const spaceKey = product => (!product?.slug || product.slug === 'mentorship' ? 'hub.mentorship.space' : `hub.mentorship.space:${product.id}`)
function remembered(product) {
  try { const v = sessionStorage.getItem(spaceKey(product)); return v && UUID.test(v) ? v : null } catch { return null }
}
function remember(product, id) {
  try { sessionStorage.setItem(spaceKey(product), id) } catch { /* private mode: the menu's pages show the list */ }
}

export function ClientSpace({ product, owned, staffView, route, page }) {
  const asked = route?.query?.get?.('space') || null
  const fromAddress = asked && UUID.test(asked) ? asked.toLowerCase() : null
  const staffSpace = staffView ? fromAddress || remembered(product) : null
  useEffect(() => {
    if (!staffView || !staffSpace) return
    remember(product, staffSpace)
    // the address carries the space, so a reload or a copied link opens the same one
    if (!fromAddress) navigate(`${route.path}?space=${staffSpace}`, { replace: true })
  }, [staffView, staffSpace, fromAddress, route?.path])

  if (staffView && !staffSpace) return <SpacePicker product={product} />
  const spaceId = staffView ? staffSpace : owned?.space?.id
  if (!spaceId) return <Failed />
  return <SpacePage key={spaceId} product={product} spaceId={spaceId} staff={staffView} page={page} />
}

function SpacePage({ product, spaceId, staff, page }) {
  const { me } = useMember()
  const viewingAs = useViewingAs()
  const nowMs = useNow()
  const phone = usePhone()
  const cs = useClientSpace(spaceId, staff, product.id)

  if (cs.status === 'loading') return <div className="ms ms--loading" aria-busy="true" />
  if (cs.status === 'error') return <Failed onRetry={cs.reload} />
  if (cs.status === 'missing') return <Missing staff={staff} />

  const { space, sessions, notes, actions, files, progress = [] } = cs.data
  const client = staff ? cs.data.client : me.person
  const open = !space.ended_at
  const ctx = {
    product, space, sessions, notes, actions, files, progress, client, first: firstName(client), staff, viewingAs, me,
    owner: staff && me.who?.staff_role === 'owner', open, nowMs, phone,
    next: open ? nextSession(sessions, nowMs, product.join_opens_minutes ?? 15) : null,
    patch: cs.patch, reload: cs.reload,
  }
  const head = eyebrowParts(client, space)
  const h1 = page === 'home' ? COPY.h1 : COPY.pageH1[page] || COPY.h1

  return (
    <div className={`ms ms--${page}${staff ? ' ms--adam' : ''}`}>
      {staff && <AdamBar ctx={ctx} />}
      <header className="ms-head">
        <span className="ms-eyebrow">{COPY.eyebrow(head.name, head.since)}</span>
        <h1 className="ms-h1">{h1}</h1>
      </header>
      {page === 'home' && <Home ctx={ctx} />}
      {page === 'sessions' && (
        <>
          {open && <SessionRow ctx={ctx} />}
          <SessionsSection ctx={ctx} heading={false} empty={COPY.noSessions} />
        </>
      )}
      {page === 'recordings' && <SessionsSection ctx={ctx} heading={false} onlyRecordings empty={COPY.noRecordings} />}
      {page === 'actions' && <ActionsSection ctx={ctx} empty />}
      {page === 'resources' && (
        <>
          <FilesCard ctx={ctx} />
          <ResourcesSection product={product} />
        </>
      )}
      {ctx.owner && page === 'home' && <EndSpace ctx={ctx} />}
    </div>
  )
}

function Home({ ctx }) {
  return (
    <>
      {ctx.open && <SessionRow ctx={ctx} />}
      <ActionsSection ctx={ctx} />
      <SessionsSection ctx={ctx} />
      <section className={ctx.open ? 'ms-row ms-row--files' : 'ms-row ms-row--single'}>
        <FilesCard ctx={ctx} />
        {ctx.open && <AskAdamCard ctx={ctx} />}
      </section>
    </>
  )
}

function Failed({ onRetry }) {
  return (
    <section className="pp-failed">
      <h1 className="pp-failed-h1">{COPY.failedH}</h1>
      <p className="pp-failed-text">{COPY.failedLine(SITE.helpEmail)}</p>
      {onRetry && <button type="button" className="pp-btn" onClick={onRetry}>{COPY.tryAgain}</button>}
    </section>
  )
}

// A space id that isn't there, or isn't open to them
function Missing({ staff }) {
  return (
    <section className="pp-failed">
      <h1 className="pp-failed-h1">{COPY.noSpace}</h1>
      {staff && <p className="pp-holding-actions"><Link to="/admin" className="pp-btn">{COPY.toAdmin}</Link></p>}
    </section>
  )
}
